/**
 * Screenable Realtime – WebSocket protocol (JSON).
 *
 * Shared by server, game client and controller. Game-agnostic: the server only
 * relays inputs and manages the session flow; game mechanics live on the player.
 *
 * Every message has a `type` field. Keep messages small – they travel over
 * mobile networks on the latency-critical path.
 */

import type { SpecialCoinId } from './scoring.js';

export const PROTOCOL_VERSION = 1;

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

/**
 * CLOSED: the player is online but the game is off by schedule (facade by day – the projection
 * only runs when it is dark). Nobody is queued; `opensAt` says when it opens again.
 */
export type GameState = 'OFFLINE' | 'CLOSED' | 'IDLE' | 'READY' | 'PLAYING' | 'GAME_OVER';

/**
 * Controller session state. CONNECTING is only used client-side (before the
 * welcome). DISCONNECTED is what the server reports for a session whose socket
 * is gone but that is still inside its reconnect grace period.
 */
export type ControllerState = 'CONNECTING' | 'WAITING' | 'READY' | 'PLAYING' | 'FINISHED' | 'DISCONNECTED';

/** Why a controller ended up in FINISHED. */
export type FinishReason =
  | 'round_over' // regular end of the round (time / game over / manual)
  | 'left' // left the queue voluntarily
  | 'ready_timeout' // did not press START in time
  | 'queue_full' // could not join, queue limit reached
  | 'aborted' // round aborted by the server (e.g. connection lost)
  | 'closed'; // the game is off by schedule (CLOSED)

/** Input actions are free-form identifiers (e.g. "flap"), validated by pattern. */
export const INPUT_ACTION_PATTERN = /^[a-z][a-z0-9_]{0,23}$/;
export const GAME_ID_PATTERN = /^[a-zA-Z0-9_-]{1,32}$/;

// ---------------------------------------------------------------------------
// WebSocket close codes (application range 4000-4999)
// ---------------------------------------------------------------------------

export const CLOSE = {
  HELLO_TIMEOUT: 4000,
  PLAYER_REPLACED: 4001,
  SESSION_TAKEN_OVER: 4002,
  VERSION: 4003,
  UNKNOWN_GAME: 4004,
  AUTH: 4005,
  INVALID: 4006,
  RATE_LIMIT: 4008,
  GAME_FULL: 4009,
  /** server: FINISHED controller was idle too long */
  IDLE: 4010,
  /** server: no heartbeat from the client */
  HEARTBEAT_TIMEOUT: 4011,
  /** client: connection detected as dead (no pong) */
  CLIENT_DEAD: 4100,
  /** client: page hidden (app/tab switch, screen off) – session keeps its place for the grace period */
  HIDDEN: 4200,
  /** client: page is being closed / navigated away */
  PAGE_CLOSED: 4201,
  /** client: the round is over and the result is on the phone – it needs no connection any more */
  DONE: 4202,
} as const;

// ---------------------------------------------------------------------------
// Common
// ---------------------------------------------------------------------------

/** Application-level ping. `rtt` = the sender's last measured RTT (ms), reported for monitoring. */
export interface PingMsg {
  type: 'ping';
  id: number;
  rtt?: number;
}

/** Answer to a ping. `st` = server wall clock (ms) for optional clock-offset estimation. */
export interface PongMsg {
  type: 'pong';
  id: number;
  st: number;
}

export interface ErrorMsg {
  type: 'error';
  code: string;
  message: string;
}

// ---------------------------------------------------------------------------
// Player (game client) -> server
// ---------------------------------------------------------------------------

export interface PlayerHelloMsg {
  type: 'player.hello';
  v: number;
  gameId: string;
  token: string;
  /** Round the player is still running locally (after a short reconnect). */
  roundId?: number;
  /** false = the game is off by schedule (default true) – see `player.schedule` */
  open?: boolean;
  /** while closed: when it opens again (ms since epoch, player clock) */
  opensAt?: number;
  /**
   * true = a game of its own for this window (`multi=1`, e.g. test links opened several times) instead of
   * the location's game; `window` = the one it got before (reload of the same tab, from the welcome's gameId)
   */
  multi?: boolean;
  window?: string;
}

/**
 * The game opens / closes by schedule (facade: projection only at night). Closing lets a running
 * round finish, people waiting are sent home (`FINISHED` / `closed`) with `opensAt`.
 */
export interface PlayerScheduleMsg {
  type: 'player.schedule';
  open: boolean;
  opensAt?: number;
}

/** Player reports end of a round (time over, game over, manual debug end). */
export interface RoundEndMsg {
  type: 'round.end';
  roundId: number;
  reason: string;
  /** Optional game result (score etc.) – opaque to the server. */
  result?: Record<string, number | string>;
}

/** Player reports the live amounts of the running round (on every change). Forwarded to the controller. */
export interface RoundScoreMsg {
  type: 'round.score';
  roundId: number;
  /** own contribution so far (€) */
  own: number;
  /** subsidy so far (€) */
  subsidy: number;
}

/** Player confirms it has applied an input. Forwarded to the controller. */
export interface InputAckMsg {
  type: 'input.ack';
  seq: number;
}

/** Periodic player metrics (for debugging/monitoring only). */
export interface PlayerStatsMsg {
  type: 'player.stats';
  fps: number;
  /** average frame time over the last window (ms) */
  ft: number;
  /** max frame time over the last window (ms) */
  ftMax: number;
  /** average delay input received -> next rendered frame (ms) */
  i2f?: number;
}

export type PlayerToServer = PingMsg | PlayerHelloMsg | RoundEndMsg | RoundScoreMsg | InputAckMsg | PlayerStatsMsg | PlayerScheduleMsg;

// ---------------------------------------------------------------------------
// Server -> player
// ---------------------------------------------------------------------------

export interface PlayerWelcomeMsg {
  type: 'player.welcome';
  gameId: string;
  /** URL that the QR code should encode. */
  controllerUrl: string;
  roundDurationMs: number;
  readyTimeoutMs: number;
  /** true if the round reported in hello is still running and was resumed */
  resumed: boolean;
}

export interface RttSummary {
  last: number;
  avg: number;
  p95: number;
  max: number;
  jitter: number;
  n: number;
}

/** Game status pushed to the player whenever it changes (plus periodic debug refresh). */
export interface GameStatusMsg {
  type: 'game.status';
  state: GameState;
  queueLength: number;
  roundId: number;
  /** ms left in the current READY / PLAYING phase (0 if none) */
  remainingMs: number;
  /** CLOSED: when the game opens again (ms since epoch, player clock), 0 = unknown */
  opensAt: number;
  controller: null | {
    sid: string;
    state: ControllerState;
    connected: boolean;
    rtt: RttSummary | null;
    inputs: number;
    reconnects: number;
  };
  /** server processing time for inputs (µs) */
  serverProcUs: { last: number; avg: number; max: number } | null;
}

export interface RoundStartMsg {
  type: 'round.start';
  roundId: number;
  /** length of the life (the round time) – starts after the countdown */
  durationMs: number;
  /** "Spiel beginnt" + 3…2…1 before the round time */
  countdownMs: number;
}

/** One highscore place (no names). */
export interface LeaderboardEntry {
  /** depot + subsidy (€) – the ranking */
  totalEur: number;
  ownEur: number;
  subsidyEur: number;
  /** when the round was played (ms since epoch, server clock) */
  at: number;
}

/** Top 5 of this location – sent after the welcome and whenever it changes. */
export interface LeaderboardMsg {
  type: 'leaderboard';
  entries: LeaderboardEntry[];
}

// --- highscore of the mobile edition (HTTP, JSON) ---------------------------------

/** the list of the standalone mobile edition (all phones together) – no location may be called like this */
export const MOBILE_BOARD = 'mobile';

/** POST /api/mobile/round when a life starts → the ticket the result is handed in with */
export interface MobileTicketResponse {
  ticket: string;
}

/** POST /api/mobile/score at the end of the life – the server computes the amount itself (shared/scoring.ts) */
export interface MobileScoreRequest {
  ticket: string;
  /** gold coins at the end (0…36) */
  coins: number;
  /** special coins collected */
  specials?: string[];
}

/** answer to the result (rank 0 = not in the top 5) and GET /api/highscore/mobile (without rank) */
export interface MobileScoreResponse {
  rank?: number;
  entries: LeaderboardEntry[];
}

export interface RoundAbortMsg {
  type: 'round.abort';
  roundId: number;
  reason: string;
}

/** Forwarded input. `sp` = server processing time until forwarding (µs). */
export interface InputForwardMsg {
  type: 'input';
  action: string;
  seq: number;
  sp: number;
}

export type ServerToPlayer =
  | PongMsg
  | ErrorMsg
  | PlayerWelcomeMsg
  | GameStatusMsg
  | RoundStartMsg
  | RoundAbortMsg
  | InputForwardMsg
  | LeaderboardMsg;

// ---------------------------------------------------------------------------
// Controller (smartphone) -> server
// ---------------------------------------------------------------------------

export interface ControllerHelloMsg {
  type: 'controller.hello';
  v: number;
  /** the game's code from the controller link (?g=…) – not the location name */
  gameId: string;
  /** reconnect token from a previous welcome (stored in localStorage) */
  session?: string;
}

/** Start the round (only valid in READY). */
export interface StartMsg {
  type: 'start';
}

/** Join the queue again (only valid in FINISHED). */
export interface JoinMsg {
  type: 'join';
}

/** Leave the queue / give up the turn. */
export interface LeaveMsg {
  type: 'leave';
}

/** Control input – `seq` is a per-session increasing sequence number. */
export interface InputMsg {
  type: 'input';
  action: string;
  seq: number;
}

export type ControllerToServer = PingMsg | ControllerHelloMsg | StartMsg | JoinMsg | LeaveMsg | InputMsg;

// ---------------------------------------------------------------------------
// Server -> controller
// ---------------------------------------------------------------------------

export interface ControllerWelcomeMsg {
  type: 'controller.welcome';
  /** the game's code */
  gameId: string;
  /** location (layout, skyline of the mobile edition) */
  location: string;
  /** secret reconnect token – store it, never display it */
  session: string;
  /** public short session id (for debugging) */
  sid: string;
  resumed: boolean;
  /** how long a waiting session keeps its place after a disconnect */
  reconnectGraceMs: number;
  /** how long the active session survives a disconnect */
  activeReconnectGraceMs: number;
  /** time to press START once it is your turn */
  readyTimeoutMs: number;
  /** call to action after the round (tracking URL), empty = not configured */
  ctaUrl: string;
}

/** The player's own result after the round – shown on the phone. */
export interface RoundResultSummary {
  coins: number;
  ownEur: number;
  subsidyEur: number;
  totalEur: number;
  /** special coins collected (shared/scoring.ts, SPECIAL_COINS) */
  specials: SpecialCoinId[];
  /** all coins of the round (special coins too) */
  max: boolean;
  /** place in the location's top 5 (1…5), 0 = not in it */
  rank: number;
}

export interface ControllerStatusMsg {
  type: 'controller.status';
  state: ControllerState;
  /** 1-based queue position (0 if not waiting) */
  position: number;
  queueLength: number;
  gameState: GameState;
  /** ms left for READY (start deadline) or PLAYING (round) */
  remainingMs: number;
  reason?: FinishReason;
  /** CLOSED: when the game opens again (ms since epoch), 0 = unknown */
  opensAt: number;
  /** FINISHED after a round of this person: their result */
  result?: RoundResultSummary;
  /** highest input seq the server has accepted for this session (for seq continuity after reconnect) */
  lastSeq: number;
}

/** Ack forwarded from the player: the input with `seq` has been applied. */
export interface ControllerAckMsg {
  type: 'input.ack';
  seq: number;
}

/** Live amounts of the running round, forwarded from the player to the active controller. */
export interface ControllerScoreMsg {
  type: 'score';
  own: number;
  subsidy: number;
}

/** Sent to the active controller roughly once per second while it is READY/PLAYING. */
export interface ControllerMetricsMsg {
  type: 'metrics';
  /** player RTT (ms) last / avg */
  pRtt: number;
  pRttAvg: number;
  fps: number;
}

export type ServerToController =
  | PongMsg
  | ErrorMsg
  | ControllerWelcomeMsg
  | ControllerStatusMsg
  | ControllerAckMsg
  | ControllerScoreMsg
  | ControllerMetricsMsg;
